// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React from "react";
import "./PredictResult.scss";
import { FontIcon } from "@fluentui/react";
import { ITag } from '../../model/applicationModels';
import FieldItemLabel from './FieldItem';

export interface IAnalyzeModelInfo {
    docType: string,
    modelId: string,
    docTypeConfidence: number,
}

export interface ITableResultItem {
    displayOrder: number,
    fieldName: string,
    type: string,
    values: {},
    rowKeys?: [],
    columnKeys: [],
}

export interface IResultItem {
    boundingBox: [],
    confidence: number,
    displayOrder: number,
    elements: [],
    fieldName: string,
    page: number,
    text: string,
    type: string,
    valueString: string,
}

export interface IPredictResultProps {
    predictions: { [key: string]: any };
    downloadPrefix?: string;
    page: number;
    tags: ITag[];
    downloadResultLabel: string;
    onAddAssetToProject?: () => void;
    onPredictionClick?: (item: IResultItem) => void;
    onTablePredictionClick?: (item: ITableResultItem, tagColor: string) => void;
    onPredictionMouseEnter?: (item: any) => void;
    onPredictionMouseLeave?: (item: any) => void;
    setHasUserChanges: (hasUserChanges: boolean, field: any) => void;
}

export interface IPredictResultState {
    hasUserChanges: boolean
}

export default class PredictResult extends React.Component<IPredictResultProps, IPredictResultState> {
    private inputRef
    constructor(props) {
        super(props);
        this.inputRef = React.createRef();
    }

    public state = {
        hasUserChanges: false,
    }

    public render() {
        const { tags, predictions } = this.props;
        const tagsDisplayOrder = tags && tags.map((tag) => tag.name);
        for (const name of Object.keys(predictions)) {
            const prediction = predictions[name];
            if (prediction != null) {
                prediction.fieldName = name;
                if (tagsDisplayOrder){
                    prediction.displayOrder = tagsDisplayOrder.indexOf(name);
                }
            }
        }
        const orderedCollection = Object.values(predictions).sort((f, s) => f.displayOrder > s.displayOrder ? 1 : ((f.displayOrder === s.displayOrder) ? 0 : -1));
        return (
            <div>
                <div className="container-items-center container-space-between results-container">
                    <h5 className="results-header">Prediction results</h5>
                </div>
                {this.props.children}
                <div className="prediction-field-header" style={{ marginTop: 28 }}>
                    <h6 className="prediction-field-header-field"> Page # / Field name / Value</h6>
                    <h6 className="prediction-field-header-confidence"> Confidence</h6>
                </div>
                <div className="prediction-header-clear"></div>

                {orderedCollection.map((item: any, key) => this.renderItem(item, key))}
            </div>
        );
    }

    private renderItem = (item: any, key: any) => {
        const tag: ITag = this.props.tags && this.props.tags.find((tag) => item.fieldName.toLocaleLowerCase() === tag.name.toLocaleLowerCase());
        if (!tag){
            return null
        }
        const style: any = {
            marginLeft: "0px",
            marginRight: "0px",
            background: this.getTagColor(tag),
        };

        if (item?.type === "array" || item?.type === "object") {
            const pageNumber = this.getPageNumberFrom(item) || 1;
            return (
                (item?.valueArray || item?.valueObject) ?
                    <div key={key}
                        onClick={() => {
                            this.onTablePredictionClick(item, this.getTagColor(tag));
                            this.onPredictionMouseLeave(item)
                        }}
                        onMouseEnter={() => this.onPredictionMouseEnter(item)}
                        onMouseLeave={() => this.onPredictionMouseLeave(item)}>
                        <li className="predictiontag-item" style={style}>
                            <div className={"predictiontag-color"}>
                                <span>{pageNumber}</span>
                            </div>
                            <div className={"predictiontag-content"}>
                                {this.getPredictionTagContent(item)}
                            </div>
                        </li>
                        <li className="predictiontag-item-label mt-0 mb-1">
                            <FontIcon className="pr-1 pl-1" iconName="Table" />
                            <span style={{ color: "rgba(255, 255, 255, 0.75)" }}>Click to validate table items</span>
                            <div className="form-check col">
                                <input
                                    disabled={true}
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={item.validated}
                                    id="flexCheckDefault" />
                            </div>
                        </li>
                    </div> :
                    <>
                        <li className="predictiontag-item" style={style}>
                            <div className={"predictiontag-color"}></div>
                            <div className={"predictiontag-content"}>
                                {this.getPredictionTagContent(item)}
                            </div>
                        </li>
                        <li className="predictiontag-item-label-null mt-0 mb-1">NULL</li>
                    </>
            )
        } else {
            return (
                <div key={key}
                    onMouseEnter={() => this.onPredictionMouseEnter(item)}
                    onMouseLeave={() => this.onPredictionMouseLeave(item)}>
                    <li className="predictiontag-item" style={style}>
                        <div className={"predictiontag-color"}>
                            <span>{item.page}</span>
                        </div>
                        <div className={"predictiontag-content"}>
                            {this.getPredictionTagContent(item)}
                        </div>
                    </li>
                    {<FieldItemLabel {...item} output={item} fieldChanged={() => this.fieldChanged(item)}/>}
                </div>
            );
        }
    }

    private fieldChanged = (field) => {
        this.setState({ hasUserChanges: true })
        this.props.setHasUserChanges(true, field);
    }

    private getTagColor = (tag: ITag): string => {
        if (tag) {
            return tag.color;
        }
        return "#999999";
    }

    private isTableTag(item): boolean {
        return (item.type === "array" || item.type === "object");
    }

    private getPredictionTagContent = (item: any) => {
        return (
            <div className={"predictiontag-name-container"}>
                <div className="predictiontag-name-body">
                    {
                        <span title={item.fieldName} className="predictiontag-name-text px-2">
                            {item.fieldName}
                        </span>
                    }
                </div>
                <div className={"predictiontag-confidence"}>
                    {isNaN(item.confidence) ? <span></span> :
                        <span>{(item.confidence * 100).toFixed(2) + "%"}</span>}
                </div>
            </div>
        );
    }

    private onTablePredictionClick = (prediction: any, tagColor) => {
        if (this.props.onTablePredictionClick) {
            this.props.onTablePredictionClick(prediction, tagColor);
        }
    }

    private onPredictionMouseEnter = (prediction: any) => {
        if (this.props.onPredictionMouseEnter) {
            this.props.onPredictionMouseEnter(prediction);
        }
    }

    private onPredictionMouseLeave = (prediction: any) => {
        if (this.props.onPredictionMouseLeave) {
            this.props.onPredictionMouseLeave(prediction);
        }
    }

    private getPostProcessedValue = (prediction: any) => {
        if (!prediction) {
            return null;
        }

        const { type, text } = prediction;
        if (type) {
            const valueType = `value${this.capitalizeFirstLetter(type)}`;
            const postProcessedValue = prediction[valueType]?.toString();
            if (typeof postProcessedValue === "string" && text !== postProcessedValue) {
                return valueType + ": " + postProcessedValue;
            }
        }

        return null;
    }

    private getPageNumberFrom = (item: any) => {
        if (item && item.hasOwnProperty("page")) {
            return item.page;
        }

        // Get page number from item's children in a recursive way.
        if (item && item.type === "object" && item.valueObject) {
            for (const property of Object.keys(item.valueObject)) {
                const pageNumber = this.getPageNumberFrom(item.valueObject[property]);
                if (pageNumber) {
                    return pageNumber;
                }
            }
        } else if (item && item.type === "array" && item.valueArray) {
            for (const element of item.valueArray) {
                const pageNumber = this.getPageNumberFrom(element);
                if (pageNumber) {
                    return pageNumber;
                }
            }
        }
    }

    private capitalizeFirstLetter = (str: string): string => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }
}
